import type { $Fetch } from 'ofetch';
import type { MatriculaGateway } from '@/application/port/out/MatriculaGateway';
import type {
  CreateMatriculaCommand,
  ListMatriculasQuery,
  TransitionMatriculaStatusCommand,
} from '@/application/port/in/dto/MatriculaCommand';
import type { Matricula } from '@/domain/model/Matricula';
import type { Page } from '@/domain/model/Page';
import type { ApiResponse, SpringPage } from '@/infrastructure/http/types';
import { assertApiSuccess } from '@/infrastructure/http/assertApiSuccess';

const PATH = '/api/v1/matriculas';

export class HttpMatriculaGateway implements MatriculaGateway {
  constructor(private readonly client: $Fetch) {}

  async list(query: ListMatriculasQuery): Promise<Page<Matricula>> {
    const params: Record<string, string | number> = {};
    if (typeof query.page === 'number') params['page'] = query.page;
    if (typeof query.size === 'number') params['size'] = query.size;
    if (query.sort) params['sort'] = query.sort;

    const res = await this.client<ApiResponse<SpringPage<Matricula>>>(PATH, {
      method: 'GET',
      params,
    });
    assertApiSuccess(res);
    const p = res.data;
    return {
      content: p.content,
      number: p.number,
      size: p.size,
      totalElements: p.totalElements,
      totalPages: p.totalPages,
      first: p.first,
      last: p.last,
    };
  }

  async getById(id: string): Promise<Matricula> {
    const res = await this.client<ApiResponse<Matricula>>(
      `${PATH}/${encodeURIComponent(id)}`,
      { method: 'GET' },
    );
    assertApiSuccess(res);
    return res.data;
  }

  async listByAluno(alunoId: string): Promise<Matricula[]> {
    const res = await this.client<ApiResponse<Matricula[]>>(
      `${PATH}/aluno/${encodeURIComponent(alunoId)}`,
      { method: 'GET' },
    );
    assertApiSuccess(res);
    return res.data;
  }

  async create(command: CreateMatriculaCommand): Promise<Matricula> {
    const res = await this.client<ApiResponse<Matricula>>(PATH, {
      method: 'POST',
      body: {
        alunoId: command.alunoId,
        courseId: command.courseId,
        cohortId: command.cohortId,
        poloId: command.poloId,
        dataInicio: command.dataInicio,
        dataFimPrevista: command.dataFimPrevista,
      },
    });
    assertApiSuccess(res);
    return res.data;
  }

  async transitionStatus(
    command: TransitionMatriculaStatusCommand,
  ): Promise<Matricula> {
    const res = await this.client<ApiResponse<Matricula>>(
      `${PATH}/${encodeURIComponent(command.id)}/status`,
      {
        method: 'PATCH',
        body: {
          status: command.status,
          motivo: command.motivo,
        },
      },
    );
    assertApiSuccess(res);
    return res.data;
  }
}
